import { FormEvent, useState } from "react";
import { Alert, Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { ConfigField, FieldDef, IConfig } from "Pages/Setup/Connect/connectShared";
import { CMIC_FIELDS } from "Pages/Setup/Connect/connectData";
import styles from "Pages/Setup/Connect/Onboarding.module.css";
import appreg1 from "Pages/Setup/Connect/img/appreg1.png";
import appreg2 from "Pages/Setup/Connect/img/appreg2.png";

interface Props {
    find: (suffix: string) => IConfig | undefined;
    onEdit: (suffix: string, secret: boolean, index: number) => void;
    onSave: (suffix: string, value: string) => Promise<boolean>;
    goTo: (view: string) => void;
}

const SERVICE_USER_SUFFIX = "cmic.api.oauth.cmicuser";

const CMIC_PERMISSION_ROWS = [
    ["AP-REST-API", "apallvouchers", "All Vouchers (Query only)", "No", "No", "No", "Yes"],
    ["AP-REST-API", "apvendor", "Vendors", "Yes", "Yes", "Yes", "Yes"],
    ["AP-REST-API", "apvouchernosc", "Regular AP Vouchers", "Yes", "No", "No", "Yes"],
    ["AP-REST-API", "unpostedvouchers", "Unposted Vouchers", "No", "Yes", "No", "Yes"],
    ["AP-REST-API", "apvoucher", "SC RFP Based Vouchers", "Yes", "No", "No", "Yes"],
    ["AP-REST-API", "postvoucher", "Post Vouchers", "Yes", "No", "No", "Yes"],
    ["AP-REST-API", "manualcheck", "Manual Checks", "Yes", "No", "No", "Yes"],
    ["AR-REST-API", "artaxitems", "Tax Codes", "No", "No", "No", "Yes"],
    ["GLRESTAPI", "glaccounts", "Accounts", "No", "No", "No", "Yes"],
    ["GLRESTAPI", "glcompany", "Companies", "No", "No", "No", "Yes"],
    ["GLRESTAPI", "rtigenericgldepts", "Departments", "No", "No", "No", "Yes"],
    ["JC-REST-API", "jctran", "Transactions", "Yes", "No", "No", "Yes"],
    ["JC-REST-API", "jcjob", "Jobs", "No", "No", "No", "Yes"],
    ["JC-REST-API", "jcjobcostcodes", "Job Phases/Cost Codes", "No", "No", "No", "Yes"],
    ["JC-REST-API", "jcjobcategory", "Job Categories", "No", "No", "No", "Yes"],
    ["JC-REST-API", "jcdetailposted", "Posted Details", "No", "No", "No", "Yes"],
    ["PM-REST-API", "pmproject", "Projects", "No", "No", "No", "Yes"],
    ["PM-REST-API", "pmkeyplayers", "Key Players", "No", "No", "No", "Yes"],
    ["PM-REST-API", "pmcompcontact", "Company Contacts", "No", "No", "No", "Yes"],
    ["PM-REST-API", "scmast", "Subcontract Headers", "No", "No", "No", "Yes"],
    ["PM-REST-API", "scsched", "Subcontract Details", "No", "No", "No", "Yes"],
    ["SYS-REST-API", "businesspartner", "Business Partners", "Yes", "Yes", "No", "Yes"]
] as const;

export default function CmicConnect({ find, onEdit, onSave, goTo }: Props) {
    const [isServiceUserOpen, setIsServiceUserOpen] = useState(false);
    const [clientId, setClientId] = useState("");
    const [username, setUsername] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const editConnectionField = (suffix: string, secret: boolean, index: number) => {
        if (suffix !== SERVICE_USER_SUFFIX) {
            onEdit(suffix, secret, index);
            return;
        }

        const value = find(suffix)?.keyValue ?? "";
        const separator = value.indexOf("||");
        setClientId(separator < 0 ? "" : value.slice(0, separator));
        setUsername(separator < 0 ? value : value.slice(separator + 2));
        setSaveError("");
        setIsServiceUserOpen(true);
    };

    const closeServiceUser = () => {
        if (!isSaving) setIsServiceUserOpen(false);
    };

    const saveServiceUser = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSaving) return;
        const client = clientId.trim();
        const user = username.trim();
        if (!client || !user || client.includes("||") || user.includes("||")) {
            setSaveError("Enter a Client ID and Username without the || separator.");
            return;
        }

        setIsSaving(true);
        setSaveError("");
        try {
            const saved = await onSave(SERVICE_USER_SUFFIX, `${client}||${user}`);
            if (saved) {
                setIsServiceUserOpen(false);
            } else {
                setSaveError("Error saving Service User. Please try again.");
            }
        } catch {
            setSaveError("Error saving Service User. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const copyText = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const requiredMissing = CMIC_FIELDS.filter((f) => f.required).filter(
        (f) => !find(f.suffix)?.keyValue
    ).length;
    const connected = requiredMissing === 0;

    return (
        <div className={styles.panel}>
            <div className={styles.phead}>
                <div className={styles.logo}>CM</div>
                <div>
                    <div className={styles.pheadName}>CMiC</div>
                    <div className={styles.pheadSub}>
                        Construction ERP · AP, AR, JC, GL, EM, PM, SYS REST APIs
                    </div>
                </div>
                {connected ? (
                    <span className={`${styles.pill} ${styles.pillGood}`}>
                        <span className={styles.dot} /> Connected
                    </span>
                ) : (
                    <span className={`${styles.pill} ${styles.pillBad}`}>
                        <span className={styles.dot} /> Not connected
                    </span>
                )}
            </div>

            <div className={styles.tabs}>
                <button className={`${styles.tab} ${styles.tabActive}`}>
                    Connect
                </button>
                <button className={styles.tab} onClick={() => goTo("secondarytest")}>
                    Test Connection
                </button>
            </div>

            <div className={styles.wz}>
                <div className={styles.wzLeft}>
                    <h2>Step 1: Create a Service User in CMiC</h2>
                    <ol className={styles.steps}>
                        <li>
                            In CMiC, create a service user (or reuse an existing
                            integration user).
                        </li>
                        <li>
                            Assign it to each company that you would like the integration to sync, as well as 
                            the master job/project security groups for those companies.
                        </li>
                        <li>
                            Grant it access to the API endpoints as demonstrated in the example to the right. 
                            If you wish, you may instead add each application (AP, AR, GL, EM, JC, PM and SYS) to the API 
                            program security bypass to mitigate the need to add new endpoints in the future. If you will be 
                            importing equipment cost data, you must add the EM-REST-API module to your API program security bypass 
                            as CMiC does not expose the needed transaction code endpoint through the UI.
                        </li>
                        <li>Once done, enter the full OAuth API URL and service user below.</li>
                    </ol>

                    {CMIC_FIELDS.map((field: FieldDef, index) => (
                        <ConfigField
                            key={field.suffix}
                            field={field}
                            config={find(field.suffix)}
                            index={index}
                            onEdit={editConnectionField}
                        />
                    ))}

                    <h2>Step 2: Register the OAuth App in CMiC</h2>
                    <ol className={styles.steps}>
                        <li>Navigate to the <b>App Registration</b> page of your API security settings.</li>
                        <li>
                            Insert a new app and enter the given values for each field:
                            <ul>
                                <li>
                                    Description: <code>Dimension CMiC Connector</code>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        className={styles.receiptCopyButton}
                                        onClick={() => copyText("Dimension CMiC Connector")}
                                    >Copy</Button>
                                </li>
                                <li>
                                    Application ID: <code>dim-cmic-shared</code>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        className={styles.receiptCopyButton}
                                        onClick={() => copyText("dim-cmic-shared")}
                                    >Copy</Button>
                                </li>
                                <li>
                                    Issuer URL: <code>https://securetoken.google.com/dim-cmic-shared</code>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        className={styles.receiptCopyButton}
                                        onClick={() => copyText("https://securetoken.google.com/dim-cmic-shared")}
                                    >Copy</Button>
                                </li>
                                <li>
                                    JSON Web Keyset URL: <code>https://www.googleapis.com/service_accounts/v1/jwk/securetoken@system.gserviceaccount.com</code>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        className={styles.receiptCopyButton}
                                        onClick={() => copyText("https://www.googleapis.com/service_accounts/v1/jwk/securetoken@system.gserviceaccount.com")}
                                    >Copy</Button>
                                </li>
                                <li>
                                    Service User: (enter your configured service user)
                                </li>
                            </ul>
                        </li>
                        <li>In the <b>Roles</b> section beneath the app, enter your service user's assigned API role.</li>
                    </ol>
                    <div className={styles.cmicOAuthScreenshots}>
                        <a
                            href={appreg1}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={appreg1}
                                alt="CMiC App Registration showing the OAuth application record and issuer URL"
                            />
                        </a>
                        <a
                            href={appreg2}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={appreg2}
                                alt="CMiC App Registration showing the JSON Web Keyset URL, service user, and authorization URL"
                            />
                        </a>
                    </div>
                </div>

                <div className={styles.wzRight}>
                    <p className={styles.rlabel}>CMiC endpoint setup guide</p>
                    <p className={styles.crumb}>
                        Confirm the service user has the following endpoint
                        permissions.
                    </p>
                    <div className={styles.cmicPermTableWrap}>
                        <table className={styles.cmicPermTable}>
                            <thead>
                                <tr>
                                    <th>Application</th>
                                    <th>Endpoint</th>
                                    <th>API Description</th>
                                    <th>Insert</th>
                                    <th>Update</th>
                                    <th>Delete</th>
                                    <th>Query</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CMIC_PERMISSION_ROWS.map((row) => (
                                    <tr key={`${row[0]}-${row[1]}`}>
                                        <td>{row[0]}</td>
                                        <td>
                                            <code>{row[1]}</code>
                                        </td>
                                        <td>{row[2]}</td>
                                        {[row[3], row[4], row[5], row[6]].map((value, i) => (
                                            <td
                                                key={`${row[0]}-${row[1]}-${i}`}
                                                className={
                                                    value === "Yes"
                                                        ? styles.cmicPermYes
                                                        : undefined
                                                }
                                            >
                                                {value}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className={styles.shotCap}>
                        Match these permissions in CMiC before saving
                        credentials.
                    </p>
                </div>
            </div>

            <Modal isOpen={isServiceUserOpen} toggle={closeServiceUser} labelledBy="cmic-service-user-title">
                <ModalHeader id="cmic-service-user-title" toggle={isSaving ? undefined : closeServiceUser}>
                    Service User
                </ModalHeader>
                <Form onSubmit={saveServiceUser}>
                    <ModalBody>
                        {saveError && <Alert color="danger">{saveError}</Alert>}
                        <FormGroup>
                            <Label for="cmic-service-company">Client ID (this is the first field you enter when logging into CMiC)</Label>
                            <Input
                                id="cmic-service-company"
                                value={clientId}
                                onChange={(event) => setClientId(event.target.value)}
                                required
                                disabled={isSaving}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="cmic-service-username">Username</Label>
                            <Input
                                id="cmic-service-username"
                                value={username}
                                onChange={(event) => setUsername(event.target.value)}
                                required
                                disabled={isSaving}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" color="secondary" onClick={closeServiceUser} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button type="submit" color="primary" disabled={isSaving}>
                            {isSaving ? "Saving..." : "Save"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <div className={styles.footbar}>
                <Button color="link" onClick={() => goTo("ramp")}>
                    ← Back to Ramp
                </Button>
                <span className={styles.saveState}>
                    {connected
                        ? "✓ CMiC credentials are set."
                        : `${requiredMissing} required value${
                              requiredMissing > 1 ? "s" : ""
                          } missing.`}
                </span>
            </div>
        </div>
    );
}
